import { Controller, ForbiddenException, Get, Query, Req, UseGuards } from '@nestjs/common';
import { ApiBearerAuth, ApiOperation, ApiTags } from '@nestjs/swagger';
import { AuthGuard, AuthenticatedRequest } from '../auth/auth.guard';
import { PlatformAnalyticsQuery, PlatformAnalyticsService } from './platform-analytics.service';

/**
 * Platform roles ONLY.
 *
 * Every endpoint here reads across all tenants, so AdminAccessGuard — which
 * grants access on the basis of being staff of the store in context — is not
 * the right gate and is deliberately not used. A store owner passing their own
 * x-store-id must never reach these.
 */
const PLATFORM_ROLES = new Set(['admin', 'super_admin', 'platform_admin', 'account_manager', 'operator']);

@ApiTags('Platform · Analytics')
@ApiBearerAuth()
@Controller('platform/analytics')
@UseGuards(AuthGuard)
export class PlatformAnalyticsController {
  constructor(private readonly analytics: PlatformAnalyticsService) {}

  private assert(request: AuthenticatedRequest): void {
    if (!request.user || !PLATFORM_ROLES.has(request.user.role)) {
      throw new ForbiddenException('Platform analytics is restricted to platform staff.');
    }
  }

  @Get('overview')
  @ApiOperation({ summary: 'GMV, orders and how many stores are actually selling' })
  overview(@Query() query: PlatformAnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.overview(query);
  }

  @Get('activation')
  @ApiOperation({ summary: 'Created → branded → catalogued → selling → established' })
  activation(@Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.activation();
  }

  @Get('churn-risk')
  @ApiOperation({ summary: 'Stores that were selling and have gone quiet, richest first' })
  churnRisk(@Query() query: PlatformAnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.churnRisk(query);
  }

  @Get('cost-to-serve')
  @ApiOperation({ summary: 'API calls, audit volume and storage per merchant against the value they produce' })
  costToServe(@Query() query: PlatformAnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.costToServe(query);
  }

  @Get('reliability')
  @ApiOperation({ summary: 'Which stores are erroring, before they raise a ticket' })
  reliability(@Query() query: PlatformAnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.reliability(query);
  }

  @Get('trend')
  trend(@Query() query: PlatformAnalyticsQuery, @Req() request: AuthenticatedRequest) {
    this.assert(request);
    return this.analytics.trend(query);
  }
}
